import { useState, useRef, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { axiosApiInstance } from 'helpers/api';
import { loadUserData } from 'store/authActions';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    OutlinedInput,
    Paper,
    Popper,
    Stack,
    Switch,
    Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import UpgradePlanCard from './UpgradePlanCard';
import User1 from 'assets/images/users/user-round.svg';
import { logout } from 'store/authActions';

// assets
import { IconLogout, IconSearch, IconSettings, IconUser } from '@tabler/icons';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);
    const userData = useSelector((state) => state.auth.userdata);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [sdm, setSdm] = useState(true);
    const [value, setValue] = useState('');
    const [notification, setNotification] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);

    const [selectedUser, setSelectedUser] = useState(-1);
    const rootRef = useRef(null);
    const menuPaperRef = useRef(null);
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);
    const handleLogout = async () => {
        await dispatch(logout());
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    let welcomeUser;
    if (userData) {
        const actualUser = userData.filter((object) => object.actual == true);
        if (actualUser && actualUser.length == 1) {
            welcomeUser = actualUser[0];
        }
    }

    const changeActualUser = async (newUser) => {
        const profileData = await axiosApiInstance.get('/api/selfservice/change_user/', {
            params: {
                user: newUser
            }
        });
        setSelectedUser(newUser);
        dispatch(loadUserData());
    };

    const handleChange = (event) => {
        changeActualUser(event.target.value);
    };

    const userRole = (roleId) => {
        if (roleId == 3) {
            return 'grāmatvedis';
        }
        if (roleId == 4) {
            return 'pārvaldnieks';
        }
        return 'skatītājs';
    };

    return (
        <>
            {userData && userData.length > 1 && (
                <Box sx={{ marginRight: '12px', display: { md: 'block', xs: 'none' } }}>
                    <Select value={selectedUser == -1 && welcomeUser ? welcomeUser.id : selectedUser} onChange={handleChange}>
                        {userData.map((user) => (
                            <MenuItem value={user.id} key={user.id}>
                                {user.display_name}
                                {user.role != 1 && ` (${userRole(user.role)})`}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}
            {userData && userData.length == 1 && (
                <Box
                    sx={{
                        marginRight: '12px',
                        fontSize: '1.325rem',
                        fontWeight: 500,
                        color: '#000',
                        display: { md: 'block', xs: 'none', maxWidth: '500px' }
                    }}
                >
                    {userData[0].display_name}
                </Box>
            )}
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
            />
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener
                                onClickAway={(e) => {
                                    const t = e.target;
                                    if (rootRef.current?.contains(t)) return;
                                    if (menuPaperRef.current?.contains(t)) return;
                                    handleClose();
                                }}
                                mouseEvent="onClick"
                            >
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]} ref={rootRef}>
                                    <Box sx={{ p: 2 }}>
                                        <Stack>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography variant="h4">Sveicināti,</Typography>
                                                <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                    {welcomeUser && welcomeUser.display_name}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                        <Divider />
                                    </Box>
                                    {userData && userData.length == 1 && (
                                        <Box
                                            sx={{
                                                marginRight: '12px',
                                                fontSize: '1.325rem',
                                                textAlign: 'center',
                                                fontWeight: 500,
                                                color: '#000',
                                                display: { md: 'none', xs: 'block' }
                                            }}
                                        >
                                            {userData[0].display_name}
                                        </Box>
                                    )}
                                    {userData && userData.length > 1 && (
                                        <Box sx={{ padding: '0 10px', display: { md: 'none', xs: 'block' } }}>
                                            <Select
                                                value={selectedUser == -1 && welcomeUser ? welcomeUser.id : selectedUser}
                                                onChange={handleChange}
                                                fullWidth
                                                MenuProps={{
                                                    // Attach a ref to the rendered Menu Paper (works in MUI v5)
                                                    PaperProps: { ref: menuPaperRef }
                                                    // If your version prefers slots:
                                                    // slotProps: { paper: { ref: menuPaperRef } },
                                                }}
                                            >
                                                {userData.map((user) => (
                                                    <MenuItem value={user.id} key={user.id}>
                                                        {user.display_name}
                                                        {user.role != 1 && ` (${userRole(user.role)})`}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    )}
                                    <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                        <Box sx={{ p: 2 }}>
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 300,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: {
                                                        minWidth: '100%'
                                                    },
                                                    '& .MuiListItemButton-root': {
                                                        mt: 0
                                                    }
                                                }}
                                            >
                                                <ListItemButton
                                                    sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                    selected={selectedIndex === 4}
                                                    onClick={handleLogout}
                                                >
                                                    <ListItemIcon>
                                                        <IconLogout stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                                                </ListItemButton>
                                            </List>
                                            <Divider />
                                        </Box>
                                    </PerfectScrollbar>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
